// Course Orchestration Types

export interface AuditLog {
  _id: string;
  step_name: string;
  status: string;
  timestamp: string;
  message: string;
  meta?: Record<string, any>;
}

export interface OrchestrationStatus {
  status: string;
  retry_count: number;
  _id: string;
  last_retry_at?: string;
  start_time: string;
  end_time?: string;
  id: string;
  error_details?: string;
}

export interface ModuleGenerationStatus {
  module_id: string;
  module_name: string;
  order: number;
  content_items_count: number;
  scenes_count: number;
  audio_generation_status: {
    _id: string;
    status: string;
    retry_count: number;
    id: string;
  };
  video_generation_status: {
    _id: string;
    status: string;
    retry_count: number;
    id: string;
  };
  audio_urls: unknown[];
  video_urls: unknown[];
  _id: string;
  id: string;
}

export interface LLMUsage {
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost: number;
  breakdown_by_model?: Record<string, any>;
  last_updated: string;
}

export interface APIUsage {
  total_tts_characters: number;
  total_tts_cost: number;
  total_stt_seconds: number;
  total_stt_cost: number;
  total_search_queries: number;
  total_search_cost: number;
  total_image_generations: number;
  total_image_cost: number;
  total_api_calls: number;
  last_updated: string;
}

export interface OverallCost {
  llm_cost: number;
  api_cost: number;
  total_cost: number;
  last_updated: string;
}

// Course Skeleton Types
export interface CourseScene {
  _id: string;
  title: string;
  scene_number: number;
  scene_type: string;
  estimated_duration: number;
  media_generation_status: 'pending' | 'processing' | 'completed' | 'failed';
  media_generation_progress: number;
  audio_url?: string;
  video_url?: string;
  has_media: boolean;
}

export interface CourseContentItem {
  _id: string;
  title: string;
  content_item_number: number;
  content_type: string;
  estimated_duration: number;
  total_scenes: number;
  completed_scenes: number;
  scenes: CourseScene[];
  pre_assessment_count?: number;
  post_assessment_count?: number;
}

export interface CourseModule {
  _id: string;
  title: string;
  module_number: number;
  description: string;
  estimated_duration: {
    hours: number;
    minutes: number;
  };
  content_items: CourseContentItem[];
  total_content_items: number;
  total_scenes: number;
  completed_scenes: number;
  completion_percentage: number;
  pre_assessment_count?: number;
  post_assessment_count?: number;
}

export interface CourseSkeleton {
  course_info: {
    _id: string;
    title: string;
    description: string;
    estimated_duration: {
      total: { hours: number; minutes: number };
      videos: { hours: number; minutes: number };
      practice: { hours: number; minutes: number };
      assessments: { hours: number; minutes: number };
    };
  };
  completion_stats: {
    total_modules: number;
    total_content_items: number;
    total_scenes: number;
    completed_scenes: number;
    completion_percentage: number;
    media_generation_complete: boolean;
  };
  modules: CourseModule[];
}

export interface UsageTracking {
  token_usage: {
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_tokens: number;
    total_estimated_cost: number;
    breakdown_by_step: Record<string, any>;
    breakdown_by_model: Record<string, any>;
  };
  api_usage: {
    costs: {
      llm: number;
      search: number;
      text_to_speech: number;
      speech_to_text: number;
      image_generation: number;
      video_generation: number;
      total: number;
    };
    statistics: {
      total_api_calls: number;
      successful_calls: number;
      failed_calls: number;
      services_used: number;
    };
    usage_by_service: Record<string, any>;
  };
  total_cost: number;
  last_updated: string;
}

export interface CourseOrchestration {
  _id: string;
  enable_knowledge_store: boolean;
  enable_example_injection_store: boolean;
  language: string;
  difficulty_level: string;
  orchestration_status: string;
  tags: string[];
  takeaways: string[];
  metadata_generation_status: OrchestrationStatus;
  course_generation_status: OrchestrationStatus;
  image_generation_status: OrchestrationStatus;
  audio_generation_status: OrchestrationStatus;
  video_generation_status: OrchestrationStatus;
  course_skeleton_generation_status?: OrchestrationStatus;
  module_content_expansion_status?: OrchestrationStatus;
  course_assessment_generation_status?: OrchestrationStatus;
  // Post-DB content validation phase. start_time is stamped after content
  // expansion completes; flips to COMPLETED together with orchestration_status.
  course_validation_status?: OrchestrationStatus;
  is_active: boolean;
  is_deleted: boolean;
  module_generation_status: ModuleGenerationStatus[];
  chat_history: unknown[];
  audit_logs: AuditLog[];
  user_course_idea: string;
  targeted_audience: string;
  knowledge_store_id: string;
  knowledge_store_only_mode: boolean;
  example_injection_enabled: boolean;
  knowledge_store_content?: string;
  pedagogy_framework?: string;
  tone: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  category_id?: string;
  category_name?: string;
  course_name?: string;
  detailed_description?: string;
  short_description?: string;
  slug?: string;
  banner_image_url?: string;
  thumbnail_image_url?: string;
  pedagogical_course_id?: string;
  total_content_items: number;
  total_modules: number;
  total_scenes: number;
  id: string;
  created_by?: string;
  created_by_user?: {
    user_id: string;
    email?: string;
    full_name?: string;
  };
  // New usage tracking structure
  token_usage_summary?: {
    total_prompt_tokens: number;
    total_completion_tokens: number;
    total_tokens: number;
    total_estimated_cost: number;
    last_updated: string;
  };
  live_llm_usage?: LLMUsage;
  live_api_usage?: APIUsage;
  overall_cost?: OverallCost;
  usage_tracking?: UsageTracking;
  // Course skeleton (appears when course_generation_status is COMPLETED)
  course_skeleton?: CourseSkeleton;
  // Raw course skeleton data from the generation pipeline
  course_skeleton_data?: any;
  // Skeleton approval workflow
  skeleton_approval?: {
    status: string;
    approved_by?: string;
    approved_by_name?: string;
    approved_at?: string;
    rejected_at?: string;
    feedback?: string;
  };
  // Course objectives generated during metadata phase
  course_objectives?: string;
  // Timestamp of last full course regeneration
  last_regenerated_at?: string;
  // Language configuration used for generation
  language_config?: {
    language_code?: string;
    device_type?: string;
    tts_config?: Record<string, any>;
    caption_config?: Record<string, any>;
  };
  // Content delivery type
  content_delivery_type?: 'html' | 'html_with_audio' | 'video_presentation' | 'avatar_video';
  // Sync mode fields
  sync_mode?: boolean;
  sync_errors?: unknown[];
  sync_completed_steps?: unknown[];
  partial_completion?: boolean;
  // Root level costs (from MongoDB document) - legacy support
  total_llm_cost?: number;
  total_search_cost?: number;
  total_tts_cost?: number;
  total_stt_cost?: number;
  total_image_cost?: number;
  total_video_cost?: number;
  total_overall_cost?: number;
  total_search_queries?: number;
}

export interface OrchestrationListResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    courseOrchestrations: CourseOrchestration[];
  };
}

export interface CourseGenerationRequest {
  user_course_idea: string;
  targeted_audience: string;
  difficulty_level: string;
  language?: string;
  language_config?: {
    language_code: string;
    tts_config?: {
      voice_name?: string;
      voice_type?: 'Neural2' | 'WaveNet' | 'Standard';
      speaking_rate?: number;
      pitch?: number;
      volume_gain_db?: number;
    };
    caption_config?: {
      enable_captions?: boolean;
      format?: 'webvtt' | 'srt';
      max_duration?: number;
      min_duration?: number;
    };
    device_type?: 'mobile' | 'desktop' | 'tablet' | 'speaker';
  };
  tone?: string;
  enable_knowledge_store?: boolean;
  knowledge_store_id?: string;
  knowledge_store_only_mode?: boolean;
  enable_example_injection_store?: boolean;
  example_injection_store_id?: string;
  example_injection_enabled?: boolean;
}

export interface OrchestrationCreateResponse {
  success: boolean;
  code: number;
  data: CourseOrchestration;
}

// Status type enums for better type safety
export enum GenerationStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  RETRYING = 'retrying'
}

export enum DifficultyLevel {
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED',
  EXPERT = 'EXPERT'
}

export enum Language {
  // International Languages
  ENGLISH = 'ENGLISH',
  SPANISH = 'SPANISH',
  FRENCH = 'FRENCH',
  GERMAN = 'GERMAN',
  PORTUGUESE = 'PORTUGUESE',
  RUSSIAN = 'RUSSIAN',
  CHINESE = 'CHINESE',
  JAPANESE = 'JAPANESE',
  KOREAN = 'KOREAN',
  ARABIC = 'ARABIC',
  TURKISH = 'TURKISH',
  ITALIAN = 'ITALIAN',
  DUTCH = 'DUTCH',
  POLISH = 'POLISH',
  SWEDISH = 'SWEDISH',
  GREEK = 'GREEK',
  HEBREW = 'HEBREW',
  
  // Indian Languages
  HINDI = 'HINDI',
  BENGALI = 'BENGALI',
  TELUGU = 'TELUGU',
  MARATHI = 'MARATHI',
  TAMIL = 'TAMIL',
  URDU = 'URDU',
  GUJARATI = 'GUJARATI',
  MALAYALAM = 'MALAYALAM',
  KANNADA = 'KANNADA',
  PUNJABI = 'PUNJABI',
  ODIA = 'ODIA',
  
  // Southeast Asian Languages
  INDONESIAN = 'INDONESIAN',
  THAI = 'THAI',
  VIETNAMESE = 'VIETNAMESE'
}

export enum Tone {
  FORMAL = 'FORMAL',
  INFORMAL = 'INFORMAL',
  PROFESSIONAL = 'PROFESSIONAL',
  FRIENDLY = 'FRIENDLY',
  PERSUASIVE = 'PERSUASIVE',
  INSPIRATIONAL = 'INSPIRATIONAL',
  NEUTRAL = 'NEUTRAL',
  EDUCATIONAL = 'EDUCATIONAL',
  EMPATHETIC = 'EMPATHETIC',
  PLAYFUL = 'PLAYFUL'
}